import { CheckoutSessionStatus, RealTimeResource } from '@nemuru-eng/js-api-client';
import { generatePath, useNavigate } from 'react-router-dom';

import { useFetchCheckoutSessionList } from '@/components/modules/CheckoutSession/List/hooks/useFetchCheckoutSessionList';
import { ListFilter } from '@/components/modules/CheckoutSession/List/types/ListFilter';
import { checkoutSessionDataGridFields } from '@/components/modules/CheckoutSession/List/utils/getDataGridFields/checkoutSessionDataGridFields';
import { CheckoutSessionsStatusTag } from '@/components/shared/components/CheckoutSessionsStatusTag/CheckoutSessionsStatusTag';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import {
  ALLOWED_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
  INITIAL_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
  STANDS_AGENT_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
} from '@/config/checkoutSessionConfig';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { isIbcStandsAgentUser } from '@/hooks/layoutConstructor/iberiaUserHelpers';
import { useCollectionScope } from '@/hooks/useCollectionScope';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

interface CheckoutSessionListProps {
  listProps?: {
    additionalFilters?: ListFilter[];
  };
}
const CheckoutSessionList = ({ listProps }: CheckoutSessionListProps) => {
  const {
    state: { lastViewedCheckoutSessionId },
    actions: { updateOperationsContext },
  } = useOperationsContext();
  const navigate = useNavigate();
  const scope = useCollectionScope();
  const { userEntity } = useUserContext();

  // The stands agent restriction goes through the status selector itself because the API returns nothing when a field
  // is filtered twice.
  const isStandsAgent = !!userEntity && isIbcStandsAgentUser(userEntity);
  const statusFilterItems = isStandsAgent
    ? STANDS_AGENT_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER
    : ALLOWED_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER;
  const defaultStatusFilterItems = isStandsAgent
    ? STANDS_AGENT_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER
    : INITIAL_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER;
  // Keeps the agent's choice but drops other statuses: the list restores the last saved selection, which is only
  // checked against every known status, so it can bring back final ones. An empty selection falls back to the defaults.
  const filterStatusesValue = (statuses: string[]): string[] => {
    if (!isStandsAgent) return statuses;

    const activeStatuses = statuses.filter((status) =>
      STANDS_AGENT_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER.includes(status as CheckoutSessionStatus),
    );
    return activeStatuses.length > 0 ? activeStatuses : STANDS_AGENT_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER;
  };

  const { fetchList, fetchXLSXExport } = useFetchCheckoutSessionList({
    additionalFilters: listProps?.additionalFilters,
  });

  return (
    <MultiFilteredList
      fetchList={fetchList}
      getXLSXExport={fetchXLSXExport}
      dataGridFields={checkoutSessionDataGridFields()}
      liveUpdates={{ resource: RealTimeResource.CHECKOUT_SESSION_LIST, scope }}
      onRowClick={(selectedRow) => {
        updateOperationsContext({ lastViewedCheckoutSessionId: selectedRow.id });
        navigate(generatePath(DYNAMIC_PAGE_PATHS.CHECKOUT_SESSION_DETAIL, { checkoutSessionId: selectedRow.id }));
      }}
      getRowClassName={(params) => {
        if (params.row.id === lastViewedCheckoutSessionId) return 'last-viewed-application';
        else return '';
      }}
      multiselectProps={{
        field: 'checkout_session_status',
        label: <Text tId={'STATUS'} />,
        defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
        items: statusFilterItems,
        checkBoxRender: (value) => {
          return <CheckoutSessionsStatusTag status={value} />;
        },
        defaultItems: defaultStatusFilterItems,
        filtersValueHandler: filterStatusesValue,
      }}
      dateFilterProps={{
        dateField: 'updated_at',
      }}
      defaultOrderBy={'updated_at'}
      showExport={false}
      disableSelectionOnClick
    />
  );
};

export const CheckoutSessionListModule = withPermissionsModule(CheckoutSessionList);
